import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Copy, CheckCircle } from 'lucide-react';
import { useClarityModalTracking } from '../../../hooks/useClarityModalTracking';
import { trackConversion } from '../../../config/clarity';

/**
 * SignupSuccessModal - Displays a success message after a successful signup
 * Shows the order reference number and allows the user to copy it to clipboard
 */
const SignupSuccessModal = ({ isOpen, onClose, referenceNumber }) => {
  const [copied, setCopied] = useState(false);
  
  // Track modal open/close
  useClarityModalTracking('signup_success_modal', isOpen, {
    referenceNumber
  });
  
  // Track conversion when success modal opens
  useEffect(() => {
    if (isOpen) {
      trackConversion('signup_completed', {
        referenceNumber,
        timestamp: new Date().toISOString()
      });
    }
  }, [isOpen, referenceNumber]);

  // Copy reference number to clipboard
  const copyToClipboard = () => {
    if (navigator.clipboard && referenceNumber) {
      navigator.clipboard.writeText(referenceNumber)
        .then(() => {
          setCopied(true);
          // Reset copied state after 2 seconds
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy: ', err);
        });
    }
  };

  // Animation variants
  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { duration: 0.3 }
    },
    exit: { 
      opacity: 0,
      transition: { duration: 0.3, delay: 0.1 }
    }
  };
  
  const modalVariants = {
    hidden: { 
      opacity: 0,
      scale: 0.9
    },
    visible: { 
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    },
    exit: { 
      opacity: 0,
      scale: 0.9,
      transition: { 
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center">
          {/* Semi-transparent Backdrop */}
          <motion.div
            className="fixed inset-0 bg-gray-900/90 backdrop-blur-sm"
            variants={backdropVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose} // Close when clicking outside
          />
          
          {/* Modal */}
          <motion.div
            className="relative z-10 bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
          >
            {/* Success Icon */}
            <div className="bg-gradient-to-r from-green-600 to-green-500 p-6 flex justify-center">
              <div className="bg-white rounded-full p-3">
                <Check className="h-10 w-10 text-green-500" />
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <h2 className="text-xl font-bold text-white mb-2">Order Submitted Successfully!</h2>
              <p className="text-gray-300 mb-6">
                Thank you for your order. We've received your application and will process it shortly.
                Please keep your reference number for future inquiries.
              </p>
              
              {/* Reference Number */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Your Reference Number
                </label>
                <div className="flex">
                  <div className="flex-1 bg-gray-700 p-3 rounded-l-lg border border-gray-600 text-white font-mono">
                    {referenceNumber || 'WN-0000-NEW-000000000'}
                  </div>
                  <button
                    className={`px-3 rounded-r-lg flex items-center justify-center transition-colors ${
                      copied 
                        ? 'bg-green-600 text-white' 
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                    onClick={copyToClipboard}
                    aria-label="Copy to clipboard"
                  >
                    {copied ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                  </button>
                </div>
                {copied && (
                  <p className="text-green-400 text-xs mt-1">Copied to clipboard!</p>
                )}
              </div>
              
              {/* What's Next Section */}
              <div className="bg-gray-700/50 rounded-lg p-4 mb-6">
                <h3 className="text-white font-medium mb-2">What's Next?</h3>
                <ul className="text-gray-300 text-sm space-y-2">
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Our team will contact you within 24-48 hours to confirm your order.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>You'll receive an email confirmation with your order details.</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                    <span>We'll schedule your installation at your preferred date and time.</span>
                  </li>
                </ul>
              </div>
              
              {/* Close Button */}
              <button
                className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-colors"
                onClick={onClose}
              >
                Close
              </button>
            </div>
            
            {/* Close Button (X) */}
            <button
              className="absolute top-4 right-4 text-white p-1 rounded-full hover:bg-gray-700/50 transition-colors"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SignupSuccessModal;